import React, {FC, PropsWithChildren, useEffect} from 'react';
import {observer} from 'mobx-react-lite';
import {useService} from '@yosmanyga/di';
import {Button, Container, Icon, Text, useIcons} from '@yosmanyga/rich-ui';
import {MapController, ProfileController} from '../../controller';
import {Basic} from '../Payment/Basic';

const states = ['pin'];

export const Map = observer(
    ({
        templates,
    }: {
        templates: {
            layout: FC<PropsWithChildren>;
        };
    }) => {
        const icons = useIcons();

        const profileController = useService(ProfileController, []);
        const mapController = useService(MapController, []);

        useEffect(() => {
            (async () => {
                await profileController.load();

                if (profileController.isBasic()) {
                    return;
                }

                await mapController.load();
            })();
        }, [profileController, mapController]);

        if (
            !profileController.loaded
            || (!profileController.isBasic() && !mapController.loaded)
        ) {
            return <templates.layout/>;
        }

        if (profileController.isBasic()) {
            return <Basic
                templates={{
                    layout: ({children}) => {
                        return <templates.layout>
                            {children}
                        </templates.layout>
                    }
                }}
                onGoToPayment={() => {
                    //
                }}
            />
        }

        return <templates.layout>
            {Object.entries(mapController.map.places).map(([, place], i) => {
                let payload = mapController.map.placePayloads.find(
                    (payload) => payload.definition === place.definition
                );

                if (
                    typeof payload === 'undefined'
                    || typeof payload.expanded === 'undefined'
                ) {
                    // Expanded by default
                    payload = {
                        definition: '',
                        expanded: true
                    }
                }

                return <Container
                    key={place.definition}
                    margin={{
                        top: i !== 0 ? 3 : undefined,
                    }}
                >
                    <Container
                        flow="row"
                        align={{
                            main: 'space-between',
                            cross: 'center',
                        }}
                    >
                        <Container
                            flow="row"
                            align={{
                                cross: 'center',
                            }}
                        >
                            <Button
                                intent={place.current ? "active" : undefined}
                                onClick={async () => {
                                    await mapController.setPlacePayload(
                                        place.definition,
                                        !payload?.expanded
                                    );
                                }}
                            >
                                <PlaceIcon expanded={payload?.expanded}/>
                            </Button>
                            <Text
                                margin={{
                                    left: 1
                                }}
                                width='60%'
                            >
                                {place.name}
                            </Text>
                        </Container>

                        <Container
                            flow="row"
                            align={{
                                cross: 'center',
                            }}
                        >
                            <Button
                                intent={!mapController.route || !mapController.isFirst(place.definition) ? 'active' : undefined}
                                onClick={async () => {
                                    await mapController.clearRoute();

                                    await mapController.setPlacePayload(place.definition, true);
                                }}
                            >
                                <Icon
                                    component={icons.objects.list}
                                    size='xs'
                                />
                            </Button>
                            <Button
                                intent={mapController.route && mapController.isFirst(place.definition) ? 'active' : undefined}
                                margin={{
                                    left: 0.5,
                                }}
                                onClick={async () => {
                                    await mapController.resolveRoute(place.definition);

                                    await mapController.setPlacePayload(place.definition, true);
                                }}
                            >
                                <Icon
                                    component={icons.objects.route}
                                    size='xs'
                                />
                            </Button>
                        </Container>
                    </Container>

                    {payload?.expanded
                        && (mapController.route && mapController.isFirst(place.definition)
                                ? <Container>
                                    {Object.entries(mapController.route).map(([, step], i) => {
                                        if (i === 0) {
                                            return null;
                                        }

                                        return (
                                            <Container
                                                key={i}
                                                flow="row"
                                                align={{
                                                    cross: 'center',
                                                }}
                                            >
                                                <Icon
                                                    component={
                                                        step.type === 'link'
                                                            ? icons.objects.link
                                                            : icons.objects.place
                                                    }
                                                    size='xs'
                                                    margin={{
                                                        left: 0.5,
                                                    }}
                                                />
                                                <Text
                                                    margin={{
                                                        left: 0.5,
                                                    }}
                                                >
                                                    {step.name}
                                                </Text>
                                            </Container>
                                        );
                                    })}
                                </Container>
                                : <Container
                                    margin={{
                                        top: 0.5,
                                    }}
                                >
                                    {Object.entries(mapController.map.matters).map(
                                        ([, matter]) => {
                                            if (matter.place !== place.definition) {
                                                return null;
                                            }

                                            const payload = mapController.map.matterPayloads.find(
                                                (payload) => payload.definition === matter.definition
                                            );

                                            return (
                                                <Container
                                                    key={matter.definition}
                                                    flow="row"
                                                    align={{
                                                        cross: 'center',
                                                    }}
                                                >
                                                    <Button
                                                        onClick={async () => {
                                                            let update;

                                                            // No icon?
                                                            if (!payload?.icon) {
                                                                // First icon
                                                                update = states[0];
                                                            } else {
                                                                const index = states.indexOf(payload.icon);
                                                                const length = states.length;
                                                                // Last icon?
                                                                if (index === length - 1) {
                                                                    // No icon
                                                                    update = '';
                                                                } else {
                                                                    // Next icon
                                                                    update = states[index + 1];
                                                                }
                                                            }

                                                            await mapController.setMatterPayload(
                                                                matter.definition,
                                                                update
                                                            );
                                                        }}
                                                    >
                                                        <MatterIcon icon={payload?.icon}/>
                                                    </Button>
                                                    <Text
                                                        margin={{
                                                            left: 0.5,
                                                        }}
                                                    >
                                                        {matter.name}
                                                    </Text>
                                                </Container>
                                            );
                                        }
                                    )}
                                </Container>
                        )
                    }
                </Container>
            })}
        </templates.layout>
    }
);

const PlaceIcon = ({expanded}: { expanded?: boolean }) => {
    const icons = useIcons();

    let component;

    switch (expanded) {
        case true:
            component = icons.states.expanded;

            break;
        default:
            component = icons.states.collapsed;

            break;
    }

    return <Icon
        component={component}
        size='xs'
    />;
};

const MatterIcon = ({icon}: { icon?: string }) => {
    const icons = useIcons();

    let component;

    switch (icon) {
        case 'unresolved':
            component = icons.objects.help;

            break;
        case 'pin':
            component = icons.objects.pin;

            break;
        default:
            component = icons.states.unchecked;

            break;
    }

    return <Icon
        component={component}
        size='xs'
    />;
};
